'use client';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { defineMessages, useIntl } from 'react-intl';
import type { MessageTag, NoMessageValues } from 'react-intl';
import { Link } from '@packages/modules/src/Link/Link';
import { Checkbox } from '@packages/shared/src/ui/Checkbox/Checkbox';

export const messages = defineMessages<{
  readonly agbCheckbox: { readonly agb: MessageTag; readonly dataProtection: MessageTag };
  readonly agbUnchecked: NoMessageValues;
}>({
  agbCheckbox: {
    id: 'BusinessTerms.AgbCheckbox',
    defaultMessage: `JA, ich stimme den <agb>AGB</agb> für bereits getätigte und künftige Bestellungen zu.\n
      Ich nehme die Bestimmungen zum <agb>Widerrufsrecht</agb> und die <dataProtection>Datenschutzhinweise</dataProtection> zur Kenntnis.`,
  },
  agbUnchecked: {
    id: 'BusinessTerms.AgbCheckbox.uncheckedError',
    defaultMessage:
      'Bitte stimmen Sie unseren AGB zu, indem Sie das entsprechende Kontrollkästchen markieren.',
  },
});

export type BusinessTermsCheckboxProps = {
  isChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  /** Show the inline error below the checkbox (submit attempted while unchecked) */
  showUncheckedError?: boolean;
};

/**
 * The retroactive AGB consent as a checkbox. Rendered when the basket requires
 * the customer to reconfirm the terms and conditions before placing the order.
 */
export const BusinessTermsCheckbox = ({
  isChecked = false,
  onCheckedChange,
  showUncheckedError = false,
}: BusinessTermsCheckboxProps) => {
  const {
    order: { externalUrls },
  } = useConfig();
  const { locale, formatMessage } = useIntl();

  return (
    <Checkbox
      checked={isChecked}
      onChange={(event) => onCheckedChange?.(event.target.checked)}
      error={showUncheckedError}
      errorMessage={formatMessage(messages.agbUnchecked)}
      containerClassName="items-start text-left"
    >
      <span className="text-size-body-xs">
        {formatMessage(messages.agbCheckbox, {
          agb: (chunks: React.ReactNode) => (
            <Link
              href={getLocalizedPath(externalUrls.agb, locale)}
              className="text-color-text-darktransparent cursor-pointer underline"
              openInNewTab
            >
              {chunks}
            </Link>
          ),
          dataProtection: (chunks: React.ReactNode) => (
            <Link
              href={getLocalizedPath(externalUrls.dataProtection, locale)}
              className="text-color-text-darktransparent cursor-pointer underline"
              openInNewTab
            >
              {chunks}
            </Link>
          ),
        })}
      </span>
    </Checkbox>
  );
};
